import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Headphones, CheckCircle2, TrendingUp, Trophy, Star, CheckCheck, Clock, CalendarCheck2, Briefcase, MapPin, Radio, Loader2, IndianRupee } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { CLEANER_STATS } from '../mock/data';
import BottomNav from '../components/BottomNav';
import DesktopHeader from '../components/DesktopHeader';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const CleanerDashboard = () => {
  const nav = useNavigate();
  const { user } = useApp();
  const [online, setOnline] = useState(true);
  const [pending, setPending] = useState([]);
  const [claimingId, setClaimingId] = useState(null);
  const pollRef = useRef(null);
  const s = CLEANER_STATS;

  const loadPending = async () => {
    try {
      const { data } = await axios.get(`${API}/cleaner/pending-jobs`, authCfg());
      setPending(data);
    } catch (e) {
      if (e?.response?.status === 401) nav('/cleaner/login');
    }
  };

  useEffect(() => {
    loadPending();
    if (online) pollRef.current = setInterval(loadPending, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, [online]);

  const accept = async (id) => {
    if (claimingId) return;
    setClaimingId(id);
    setPending((prev) => prev.filter((b) => b.id !== id));
    try {
      await axios.post(`${API}/cleaner/accept-job/${id}`, {}, authCfg());
      nav('/cleaner/jobs');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Job already taken');
      loadPending();
    } finally {
      setClaimingId(null);
    }
  };

  const iconFor = (label) => {
    if (label === 'Customer rating') return Star;
    if (label === 'Job completion') return CheckCheck;
    if (label === 'On-time arrival') return Clock;
    return CalendarCheck2;
  };

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 md:pb-8">
      <DesktopHeader />

      <div className="max-w-[1200px] mx-auto px-5 md:px-6 pt-4 md:pt-6">
        {/* Header banner */}
        <div className="rounded-2xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #2E7D32 0%, #388E3C 60%, #43A047 100%)' }}>
          <div className="px-5 md:px-8 py-5 md:py-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-white text-xl md:text-3xl font-extrabold flex items-center gap-2">Hi, {user?.name?.split(' ')[0] || 'Cleaner'} <span>👋</span></h1>
              <div className="mt-2 inline-flex items-center gap-2 bg-white/15 border border-white/25 rounded-full px-3 py-1">
                <span className={`w-2 h-2 rounded-full ${online ? 'bg-[#7DFB8E]' : 'bg-neutral-300'}`} />
                <span className="text-white text-xs font-medium">{online ? 'Online — receiving jobs' : 'Offline'}</span>
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                <QuickBtn icon={CalendarDays} label="Leaves" />
                <QuickBtn icon={Headphones} label="Support" />
                <QuickBtn icon={CheckCircle2} label="Attendance" onClick={() => nav('/cleaner/attendance')} />
              </div>
            </div>
            <button onClick={() => setOnline(!online)}
              className={`w-14 h-8 rounded-full flex items-center px-1 transition-colors flex-shrink-0 ${online ? 'bg-[#66BB6A]' : 'bg-neutral-300'}`}>
              <span className={`w-6 h-6 rounded-full bg-white shadow transform transition-transform ${online ? 'translate-x-6' : ''}`} />
            </button>
          </div>
        </div>

        {/* Content grid */}
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          {/* Performance card */}
          <div className="md:col-span-2 bg-white border border-neutral-200 rounded-2xl p-5">
            <p className="text-xs text-neutral-500 font-semibold tracking-wider">PERFORMANCE SCORE</p>
            <div className="mt-2 flex items-start justify-between">
              <div>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-[#EA580C]">{s.performanceScore}</span>
                  <span className="text-neutral-500 font-semibold">/100</span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="inline-flex items-center gap-1 bg-[#FFF3E0] text-[#EA580C] text-[11px] font-bold px-2.5 py-1 rounded-full">
                    <TrendingUp className="w-3 h-3" /> {s.status}
                  </div>
                  <p className="text-xs text-neutral-500">{s.ratingsMessage}</p>
                </div>
              </div>
              <div className="w-16 h-16 rounded-full border-4 border-[#EA580C] flex items-center justify-center">
                <Trophy className="w-8 h-8 text-[#EA580C]" />
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {s.breakdown.map((b) => {
                const Icon = iconFor(b.label);
                const pct = Math.min(100, (b.value / b.max) * 100);
                const color = b.label === 'Attendance' ? '#EF4444' : '#2E7D32';
                return (
                  <div key={b.label}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-neutral-700" />
                        <span className="text-sm font-semibold">{b.label}</span>
                        <span className="text-xs text-neutral-500">· {b.weight}%</span>
                      </div>
                      <span className="text-xs text-neutral-500 font-semibold">{b.value}</span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-neutral-100 grid grid-cols-3 gap-2">
              <MiniStat icon={Briefcase} value={s.jobsDone} label="Jobs done" />
              <MiniStat icon={CalendarDays} value={s.attendance} label="Attendance" />
              <MiniStat icon={Clock} value={s.onTime} label="On-time" />
            </div>
          </div>

          {/* Live jobs */}
          <div className="bg-white border border-neutral-200 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <h2 className="font-bold text-sm">Live jobs</h2>
                {online && (
                  <span data-testid="dashboard-live-badge" className="inline-flex items-center gap-1 bg-[#E8F5E9] text-[#2E7D32] text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                    <span className="w-1 h-1 rounded-full bg-[#2E7D32] animate-pulse" /> Live
                  </span>
                )}
              </div>
              <button onClick={() => nav('/cleaner/jobs')} className="text-xs text-[#2E7D32] font-semibold hover:underline">View all</button>
            </div>
            <p className="text-[11px] text-neutral-500 mb-3">{pending.length} pending · first to accept wins</p>
            <div className="space-y-2 max-h-[360px] overflow-y-auto">
              {pending.length === 0 ? (
                <div className="border border-dashed border-neutral-200 rounded-lg p-6 text-center">
                  <Radio className="w-6 h-6 text-neutral-300 mx-auto" />
                  <p className="text-xs text-neutral-500 mt-1.5">Stay online — new jobs appear here.</p>
                </div>
              ) : pending.slice(0, 6).map((j) => {
                const svc = j.items?.[0]?.name || 'Cleaning service';
                const claiming = claimingId === j.id;
                return (
                  <div key={j.id} data-testid={`dashboard-job-${j.id}`} className="border border-neutral-100 rounded-lg p-3 hover:border-[#2E7D32] transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="min-w-0 flex-1 pr-2">
                        <p className="font-bold text-sm truncate">{svc}</p>
                        <p className="text-[11px] text-neutral-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{j.address}</p>
                        <p className="text-[11px] text-neutral-500 mt-0.5">{j.date} · {j.slot}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-extrabold text-sm text-[#EA580C] flex items-center justify-end"><IndianRupee className="w-3 h-3" />{j.total}</p>
                        <button
                          data-testid={`dashboard-accept-${j.id}`}
                          onClick={() => accept(j.id)}
                          disabled={claiming}
                          className="mt-1 text-[10px] bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-60 text-white px-2 py-1 rounded-full font-semibold transition-colors inline-flex items-center gap-1">
                          {claiming ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : null}
                          {claiming ? '…' : 'Accept'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

const QuickBtn = ({ icon: Icon, label, onClick }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 bg-white/15 border border-white/25 hover:bg-white/25 transition-colors rounded-full px-3 py-1.5 text-white text-xs font-semibold">
    <Icon className="w-3.5 h-3.5" /> {label}
  </button>
);

const MiniStat = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-2">
    <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center">
      <Icon className="w-4 h-4 text-[#2E7D32]" />
    </div>
    <div>
      <p className="font-bold text-sm leading-none">{value}</p>
      <p className="text-[10px] text-neutral-500 mt-0.5">{label}</p>
    </div>
  </div>
);

export default CleanerDashboard;
